import { firstValueFrom } from 'rxjs'

import { Injectable } from '@angular/core'
import { FormControl } from '@angular/forms'

import { FixedSelectionModel } from '@app-lib/fixed-selection-model'
import { ShoppingCartFragment, ShoppingCartLineFragment } from '@app-graphql/schema'
import { CheckoutService } from '@app-services'

@Injectable({
    providedIn: 'root',
})
export class CartSelectionService {

    public selectedProductsControl = new FormControl<ShoppingCartLineFragment[]>([])

    public selectAllControl = new FormControl(false, { nonNullable: true })

    // Configured products are selected by configuration id
    public selectedConfigurationsControl = new FormControl<string[]>([], { nonNullable: true })

    public selectedRowsSelectionModel = new FixedSelectionModel<ShoppingCartLineFragment>(
        true,
        [],
        true,
        (p1, p2) => p1.id === p2.id,
    )

    constructor(
        private checkoutService: CheckoutService,
    ) {
        this.selectedConfigurationsControl.valueChanges.subscribe((configurationIds) => {
            this.checkoutService.selectedConfigurationIdsInShoppingCart$.next(configurationIds)
        })

        // Handled here instead of in the products table, so select all also works when the cart only holds
        // configured products and the products table is not rendered
        this.selectAllControl.valueChanges.subscribe(async (value) => {
            if (value) {
                await this.handleSelectAll()
                return
            }

            this.handleDeselectAll()
        })

        // Keep the selection in sync with the cart, so totals never include deleted lines or stale subtotals
        this.checkoutService.shoppingCart$.subscribe((cart) => {
            this.syncSelectionWithCart(cart)
        })
    }

    public toggleRowInSelection(elements: ShoppingCartLineFragment[]): void {
        this.selectedRowsSelectionModel.clear()
        elements.forEach((element) => {
            this.selectedRowsSelectionModel.toggle(element)
        })

        this.setSelectedItemsSubject()
    }

    public handleDeselectAll(): void {
        this.selectedRowsSelectionModel.clear()
        this.selectAllControl.setValue(false, { emitEvent: false })
        this.selectedProductsControl.setValue([])
        this.selectedConfigurationsControl.setValue([])
        this.setSelectedItemsSubject()
    }

    public async handleSelectAll(): Promise<void> {
        const { cartLine: cartlines, configurations } = await firstValueFrom(this.checkoutService.shoppingCart$)

        this.selectedRowsSelectionModel.setSelection(...cartlines)
        this.selectedProductsControl.setValue(cartlines)
        this.selectedConfigurationsControl.setValue(configurations.map((configuration) => configuration.id))
        this.selectAllControl.setValue(true, { emitEvent: false })
        this.setSelectedItemsSubject()
    }

    public get selectedCount(): number {
        return this.selectedRowsSelectionModel.selected.length + this.selectedConfigurationsControl.value.length
    }

    public setSelectedItemsSubject(): void {
        this.checkoutService.selectedItemsInShoppingCart$.next(this.selectedRowsSelectionModel.selected)
    }

    private syncSelectionWithCart(cart: ShoppingCartFragment): void {
        if (! this.selectedRowsSelectionModel.hasValue() && this.selectedConfigurationsControl.value.length === 0) {
            return
        }

        const cartLines = cart.cartLine ?? []
        const configurationIds = (cart.configurations ?? []).map((configuration) => configuration.id)

        const selectedLines = cartLines.filter((line) => this.selectedRowsSelectionModel.selected
            .some((selected) => selected.id === line.id))
        const selectedConfigurationIds = this.selectedConfigurationsControl.value
            .filter((id) => configurationIds.includes(id))

        // clear() first, setSelection() would keep the stale line objects as they match by id
        this.selectedRowsSelectionModel.clear()
        this.selectedRowsSelectionModel.select(...selectedLines)
        this.selectedProductsControl.setValue(selectedLines, { emitEvent: false })

        if (selectedConfigurationIds.length !== this.selectedConfigurationsControl.value.length) {
            this.selectedConfigurationsControl.setValue(selectedConfigurationIds)
        }

        const selectedCount = selectedLines.length + selectedConfigurationIds.length
        this.selectAllControl.setValue(
            selectedCount > 0 && selectedCount === cartLines.length + configurationIds.length,
            { emitEvent: false },
        )

        this.setSelectedItemsSubject()
    }

}
